import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Link,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  hasGivenConsent,
  acceptAllCookies,
  acceptNecessaryCookies
} from '../utils/cookieConsent';

const CookieBanner = ({ onOpenSettings }) => {
  const [showBanner, setShowBanner] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    // Show banner if user hasn't given consent yet
    const hasConsent = hasGivenConsent();
    setShowBanner(!hasConsent);
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    acceptNecessaryCookies();
    setShowBanner(false);
  };

  const handleCustomize = () => {
    onOpenSettings();
  };

  if (!showBanner) return null;

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 0, sm: '16px 16px 0 0' },
          backgroundColor: 'background.paper',
          borderTop: '3px solid',
          borderTopColor: 'primary.main'
        }}
      >
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: 2, md: 3 }
            }}
          >
            {/* Cookie notice text */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                🍪 We use cookies
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, lineHeight: 1.5 }}
              >
                We use cookies to enhance your browsing experience, analyze site traffic, 
                and provide personalized content. By clicking "Accept All", you consent to 
                our use of cookies.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Link
                  href="/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mr: 2 }}
                >
                  Cookie Policy
                </Link>
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Box>

            {/* Action buttons */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: '300px' }
              }}
            >
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'small'}
                onClick={handleAcceptAll}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  minWidth: { sm: '120px' },
                  fontWeight: 600
                }}
              >
                Accept All
              </Button>

              <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'small'}
                onClick={handleCustomize}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  minWidth: { sm: '100px' }
                }}
              >
                Customize
              </Button>
              
              <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'small'}
                onClick={handleAcceptNecessary}
                sx={{
                  flex: { xs: 1, sm: 'none' },
                  minWidth: { sm: '120px' },
                  borderColor: 'grey.400',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'grey.600',
                    backgroundColor: 'grey.50'
                  }
                }}
              >
                Necessary Only
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default CookieBanner;